import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Chip,
  Divider,
  Box,
  IconButton
} from '@material-ui/core';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  Place as PlaceIcon,
  Person as PersonIcon,
  Phone as PhoneIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import api from '../../services/api';
import toastError from '../../errors/toastError';
import { toast } from 'react-toastify';
import ConfirmationModal from '../ConfirmationModal';
import AgendamentoModal from '../AgendamentoModal';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  chip: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  info: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  icon: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  label: {
    fontWeight: 'bold',
    marginRight: theme.spacing(1),
  },
}));

const AgendamentoDetalhes = ({ open, onClose, agendamento, onEdit, onDelete, onConclude, readOnly = false }) => {
  const classes = useStyles();
  const [confirmModalOpen, setConfirmModalOpen] = React.useState(false);
  const [deletingAgendamento, setDeletingAgendamento] = React.useState(null);
  const [concludeModalOpen, setConcludeModalOpen] = React.useState(false);
  const [concluding, setConcluding] = React.useState(false);
  const [justificativa, setJustificativa] = React.useState("");
  const [justificativaError, setJustificativaError] = React.useState("");
  const [localAgendamento, setLocalAgendamento] = React.useState(agendamento);
  const [nextFollowupPromptOpen, setNextFollowupPromptOpen] = React.useState(false);
  const [nextFollowupModalOpen, setNextFollowupModalOpen] = React.useState(false);
  const [nextFollowupSeed, setNextFollowupSeed] = React.useState(null);

  React.useEffect(() => {
    setLocalAgendamento(agendamento);
  }, [agendamento]);

  if (!localAgendamento) return null;

  const handleDeleteClick = () => {
    setDeletingAgendamento(localAgendamento);
    setConfirmModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/agendamentos/${localAgendamento.id}`);
      toast.success('Agendamento deletado com sucesso!');
      setConfirmModalOpen(false);
      onDelete();
    } catch (err) {
      toastError(err);
    }
  };

  const handleConclude = async () => {
    const justification = justificativa.trim();

    if (!justification) {
      setJustificativaError("Informe uma justificativa para concluir.");
      return;
    }

    try {
      setConcluding(true);
      const { data } = await api.put(`/agendamentos/${localAgendamento.id}`, {
        status: "concluido",
        resultado: justification
      });

      setLocalAgendamento(data);
      setConcludeModalOpen(false);
      setJustificativa("");
      setJustificativaError("");
      toast.success("Follow-up concluído com sucesso!");

      if (onConclude) {
        onConclude(data);
      }

      if (data?.tipo === "followup") {
        setNextFollowupPromptOpen(true);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setConcluding(false);
    }
  };

  const handleOpenNextFollowupModal = () => {
    const baseDate = localAgendamento?.dataInicio ? moment(localAgendamento.dataInicio) : moment();
    const nextDate = baseDate.add(1, 'day').toDate();

    // Autor: GitHub Copilot | Data: 23/03/2026 | Motivo: agilizar criação do próximo follow-up após conclusão do atual.
    setNextFollowupSeed({
      tipo: 'followup',
      clienteId: localAgendamento?.clienteId || null,
      cliente: localAgendamento?.cliente || null,
      contatoId: localAgendamento?.contatoId || null,
      titulo: localAgendamento?.titulo || '',
      dataInicio: nextDate,
      dataFim: nextDate,
      prioridade: localAgendamento?.prioridade || 'media',
      status: 'pendente'
    });

    setNextFollowupPromptOpen(false);
    setNextFollowupModalOpen(true);
  };

  const handleSaveNextFollowup = () => {
    setNextFollowupModalOpen(false);
    setNextFollowupSeed(null);
    if (onConclude) {
      onConclude(localAgendamento);
    }
  };

  const canConclude =
    !readOnly &&
    localAgendamento.tipo === "followup" &&
    !["concluido", "cancelado"].includes(localAgendamento.status);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendente':
        return { backgroundColor: '#ff9800', color: '#fff' };
      case 'em_andamento':
        return { backgroundColor: '#2196f3', color: '#fff' };
      case 'concluido':
        return { backgroundColor: '#4caf50', color: '#fff' };
      case 'cancelado':
        return { backgroundColor: '#f44336', color: '#fff' };
      default:
        return { backgroundColor: '#757575', color: '#fff' };
    }
  };

  const getPrioridadeColor = (prioridade) => {
    switch (prioridade) {
      case 'baixa':
        return { backgroundColor: '#4caf50', color: '#fff' };
      case 'media':
        return { backgroundColor: '#ff9800', color: '#fff' };
      case 'alta':
        return { backgroundColor: '#f44336', color: '#fff' };
      case 'urgente':
        return { backgroundColor: '#d32f2f', color: '#fff' };
      default:
        return { backgroundColor: '#757575', color: '#fff' };
    }
  };

  const getTipoLabel = (tipo) => {
    const tipos = {
      reuniao: 'Reunião',
      ligacao: 'Ligação',
      visita: 'Visita',
      followup: 'Follow-up',
      tarefa: 'Tarefa',
      outro: 'Outro',
    };
    return tipos[tipo] || tipo;
  };

  const getStatusLabel = (status) => {
    const statuses = {
      pendente: 'Pendente',
      em_andamento: 'Em Andamento',
      concluido: 'Concluído',
      cancelado: 'Cancelado',
    };
    return statuses[status] || status;
  };

  const getPrioridadeLabel = (prioridade) => {
    const prioridades = {
      baixa: 'Baixa',
      media: 'Média',
      alta: 'Alta',
      urgente: 'Urgente',
    };
    return prioridades[prioridade] || prioridade;
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <div className={classes.header}>
            <Typography variant="h6">{localAgendamento.titulo}</Typography>
            <div>
              {canConclude && (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => setConcludeModalOpen(true)}
                  style={{ marginRight: 8 }}
                >
                  Concluir
                </Button>
              )}
              {!readOnly && (
                <>
                  <IconButton onClick={onEdit} size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={handleDeleteClick} size="small" color="secondary">
                    <DeleteIcon />
                  </IconButton>
                </>
              )}
            </div>
          </div>
        </DialogTitle>

        <DialogContent dividers>
          <Box className={classes.section}>
            <Chip
              label={getStatusLabel(localAgendamento.status)}
              size="small"
              className={classes.chip}
              style={getStatusColor(localAgendamento.status)}
            />
            <Chip
              label={getTipoLabel(localAgendamento.tipo)}
              variant="outlined"
              size="small"
              className={classes.chip}
            />
            <Chip
              label={getPrioridadeLabel(localAgendamento.prioridade)}
              size="small"
              style={getPrioridadeColor(localAgendamento.prioridade)}
            />
          </Box>

          <Divider />

          <Box className={classes.section} mt={2}>
            <div className={classes.info}>
              <EventIcon className={classes.icon} />
              <Typography>
                <span className={classes.label}>{localAgendamento.tipo === 'followup' ? 'Data:' : 'Início:'}</span>
                {moment(localAgendamento.dataInicio).format('DD/MM/YYYY HH:mm')}
              </Typography>
            </div>
            {localAgendamento.tipo !== 'followup' && (
              <>
                <div className={classes.info}>
                  <EventIcon className={classes.icon} />
                  <Typography>
                    <span className={classes.label}>Término:</span>
                    {moment(localAgendamento.dataFim).format('DD/MM/YYYY HH:mm')}
                  </Typography>
                </div>
                <div className={classes.info}>
                  <Typography variant="body2" color="textSecondary" style={{ marginLeft: 32 }}>
                    Duração: {moment(localAgendamento.dataFim).diff(moment(localAgendamento.dataInicio), 'minutes')} minutos
                  </Typography>
                </div>
              </>
            )}
          </Box>

          {localAgendamento.local && (
            <Box className={classes.section}>
              <div className={classes.info}>
                <PlaceIcon className={classes.icon} />
                <Typography>
                  <span className={classes.label}>Local:</span>
                  {localAgendamento.local}
                </Typography>
              </div>
            </Box>
          )}

          {localAgendamento.descricao && (
            <Box className={classes.section}>
              <Typography variant="subtitle2" gutterBottom className={classes.label}>
                Descrição:
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {localAgendamento.descricao}
              </Typography>
            </Box>
          )}

          {localAgendamento.cliente && (
            <Box className={classes.section}>
              <Divider style={{ marginBottom: 8 }} />
              <div className={classes.info}>
                <PersonIcon className={classes.icon} />
                <Typography>
                  <span className={classes.label}>Cliente:</span>
                  {localAgendamento.cliente.NOMECLI}
                </Typography>
              </div>
              {localAgendamento.contato && (
                <div className={classes.info} style={{ marginLeft: 32 }}>
                  <PhoneIcon className={classes.icon} fontSize="small" />
                  <Typography variant="body2">
                    {localAgendamento.contato.nome} - {localAgendamento.contato.telefone}
                  </Typography>
                </div>
              )}
            </Box>
          )}

          {localAgendamento.historico && (
            <Box className={classes.section}>
              <Typography variant="subtitle2" gutterBottom className={classes.label}>
                Histórico Relacionado:
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {moment(localAgendamento.historico.data_contato).format('DD/MM/YYYY')} -{' '}
                {localAgendamento.historico.resultado_visita}
              </Typography>
            </Box>
          )}

          {localAgendamento.observacoes && (
            <Box className={classes.section}>
              <Typography variant="subtitle2" gutterBottom className={classes.label}>
                Observações:
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {localAgendamento.observacoes}
              </Typography>
            </Box>
          )}

          {localAgendamento.resultado && (
            <Box className={classes.section}>
              <Typography variant="subtitle2" gutterBottom color="primary" className={classes.label}>
                Justificativa da conclusão:
              </Typography>
              <Typography variant="body2">
                {localAgendamento.resultado}
              </Typography>
            </Box>
          )}

          {localAgendamento.lembrete && (
            <Box className={classes.section}>
              <Typography variant="caption" color="textSecondary">
                Lembrete: {localAgendamento.tempoLembrete} minutos antes
                {localAgendamento.lembreteEnviado && ' (enviado)'}
              </Typography>
            </Box>
          )}

          <Divider style={{ marginTop: 16 }} />

          <Box mt={2}>
            <Typography variant="caption" color="textSecondary">
              Criado por: {localAgendamento.user?.name || 'Sistema'}
            </Typography>
            <br />
            <Typography variant="caption" color="textSecondary">
              Criado em: {moment(localAgendamento.createdAt).format('DD/MM/YYYY HH:mm')}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={concludeModalOpen}
        onClose={() => {
          if (!concluding) {
            setConcludeModalOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Concluir follow-up</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Informe a justificativa da conclusão. Esse texto ficará visível no follow-up e será adicionado ao histórico do cliente.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            margin="dense"
            label="Justificativa"
            value={justificativa}
            onChange={e => {
              setJustificativa(e.target.value);
              if (justificativaError) {
                setJustificativaError("");
              }
            }}
            error={Boolean(justificativaError)}
            helperText={justificativaError}
            disabled={concluding}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConcludeModalOpen(false);
              setJustificativa("");
              setJustificativaError("");
            }}
            disabled={concluding}
            color="default"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConclude}
            color="primary"
            variant="contained"
            disabled={concluding}
          >
            {concluding ? "Concluindo..." : "Concluir"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title="Deletar Agendamento"
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleDeleteConfirm}
      >
        Tem certeza que deseja deletar este agendamento? Esta ação não pode ser desfeita.
      </ConfirmationModal>

      <Dialog
        open={nextFollowupPromptOpen}
        onClose={() => setNextFollowupPromptOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Criar próximo follow-up?</DialogTitle>
        <DialogContent>
          <Typography>
            Deseja criar agora o próximo follow-up deste cliente?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNextFollowupPromptOpen(false)} color="default">
            Não
          </Button>
          <Button onClick={handleOpenNextFollowupModal} color="primary" variant="contained">
            Sim, criar
          </Button>
        </DialogActions>
      </Dialog>

      <AgendamentoModal
        open={nextFollowupModalOpen}
        onClose={() => {
          setNextFollowupModalOpen(false);
          setNextFollowupSeed(null);
        }}
        agendamento={nextFollowupSeed}
        forceFollowupType
        onSave={handleSaveNextFollowup}
      />
    </>
  );
};

export default AgendamentoDetalhes;
